import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';

import {Handler, HandlerOf, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CellsConversationsDragItem} from '../../../../helpers/conversationCellHelpers';
import {
  buildPrivateInboxCounterKey,
  discussionsCounterKey,
  selfAssignedCounterKey,
  selfInboxCounterKey,
  sharedWithMeCounterKey,
} from '../../../../helpers/counterHelpers';
import {buildTeammateInboxPath, buildTeammatePath} from '../../../../helpers/modelHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {CountersVisibilityModesEnum} from '../../../../models/api/preferencesModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {VisualWorkspaceFolderNestedItem} from '../../../../models/api/visualWorkspaceModel';
import {WorkspaceFolderNestedLabelsEnum, WorkspaceItemTypesEnum} from '../../../../models/api/workspaceModel';
import {InboxesFiltersEnum, TeammatesFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {WorkspaceDisplaySectionTrackingTypesEnum} from '../../../../services/tracking';
import {ResourceMap} from '../../../../store/appTypes';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {WorkspaceGenericItemContainer} from '../workspaceGenericItemContainer';
import {WorkspaceInboxItemContainer} from '../workspaceInboxItemContainer';

/*

 * Props.
 */

interface TeammateWorkspaceInboxTagProps {
  panelId: InboxesPanelId;
  user: TeammateModel;
  onWorkspaceItemDrop: WorkspaceItemDropHandler;
  inboxes: ResourceMap<InboxModel>;
  visualWorkspaceNestedFolders: ReadonlyArray<VisualWorkspaceFolderNestedItem>;
  countersVisibility: CountersVisibilityModesEnum;
  isExpanded: boolean;
  hasInternalDiscussions: boolean;
  /** Handler to expand/collapse. If undefined, the chevron will be hidden and the content will not be expanded. */
  onToggleExpandClick?: Handler;
  canConversationsDrop: PredicateOf<CellsConversationsDragItem>;
  onConversationsDrop: HandlerOf<CellsConversationsDragItem>;
}

/*
 * Component.
 */

export const TeammateWorkspaceInboxTag: FC<TeammateWorkspaceInboxTagProps> = (props) => (
  <>
    {/* Inbox. */}
    <WorkspaceGenericItemContainer
      shouldHideMoreMenu={false}
      canBeHidden={false}
      trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.INBOX_VIEW}
      shouldAlphabetizeWorkspace={false}
      iconName="inboxSmall"
      selectedIconName="inboxFilledSmall"
      to={buildTeammatePath(props.user.id, TeammatesFoldersEnum.INBOX, InboxesFiltersEnum.OPEN)}
      counterKey={selfInboxCounterKey}
      interactionId="workspace-user-inbox"
      workspaceDragItem={undefined}
      {...props}
    >
      <Trans id="workspace-inbox">Inbox</Trans>
    </WorkspaceGenericItemContainer>

    {/* Content of the "Inbox" section. */}
    {maybeRenderExpandedContent(props)}
  </>
);

function maybeRenderExpandedContent(props: TeammateWorkspaceInboxTagProps) {
  // No need to render anything if this workspace is collapsed.
  const {
    panelId,
    isExpanded,
    user,
    inboxes,
    countersVisibility,
    hasInternalDiscussions,
    onToggleExpandClick,
    visualWorkspaceNestedFolders,
    onWorkspaceItemDrop,
  } = props;
  if (!isExpanded || !onToggleExpandClick) {
    return null;
  }

  const lastIndex = visualWorkspaceNestedFolders.length - 1;

  // Otherwise, render the known folders and teammate inboxes.
  return (
    _(visualWorkspaceNestedFolders)
      // These cannot be hidden but we filter for isSelected since it is good practice.
      .filter(({isSelected}) => isSelected)
      .map((item, index) => {
        if (item.type === WorkspaceItemTypesEnum.INBOX) {
          const inbox = inboxes[item.id];
          if (!inbox) {
            return null;
          }

          return (
            <WorkspaceInboxItemContainer
              shouldAlphabetizeWorkspace={false}
              shouldHideMoreMenu={false}
              canBeHidden={false}
              trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.INDIV_SUB_INBOX}
              onWorkspaceItemDrop={onWorkspaceItemDrop}
              key={item.id}
              panelId={panelId}
              inbox={inbox}
              to={buildTeammateInboxPath(inbox.id, InboxesFiltersEnum.OPEN)}
              counterKey={buildPrivateInboxCounterKey(inbox.id)}
              countersVisibility={countersVisibility}
              indentLevel={1}
              workspaceDragItem={{
                type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
                id: inbox.id,
                isLast: index === lastIndex,
              }}
              hasEditPermission={false}
            />
          );
        }

        switch (item.id) {
          case WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME:
            return (
              <WorkspaceGenericItemContainer
                shouldAlphabetizeWorkspace={false}
                canBeHidden={false}
                shouldHideMoreMenu={false}
                trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.LABELS}
                key={item.id}
                panelId={panelId}
                onWorkspaceItemDrop={onWorkspaceItemDrop}
                to={buildTeammatePath(user.id, TeammatesFoldersEnum.ASSIGNED, InboxesFiltersEnum.OPEN)}
                iconName="assignSmall"
                selectedIconName="assignFilledSmall"
                counterKey={selfAssignedCounterKey}
                countersVisibility={countersVisibility}
                indentLevel={1}
                workspaceDragItem={{
                  type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
                  id: WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME,
                  isLast: index === lastIndex,
                }}
                interactionId="workspace-user-assigned"
              >
                <Trans id="workspace-assigned-to-me">Assigned to me</Trans>
              </WorkspaceGenericItemContainer>
            );

          case WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME: {
            return (
              <WorkspaceGenericItemContainer
                shouldAlphabetizeWorkspace={false}
                canBeHidden={false}
                shouldHideMoreMenu={false}
                trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.LABELS}
                key={item.id}
                panelId={panelId}
                onWorkspaceItemDrop={onWorkspaceItemDrop}
                to={buildTeammatePath(user.id, TeammatesFoldersEnum.SHARED_WITH_ME, InboxesFiltersEnum.OPEN)}
                iconName="teammatesSmall"
                selectedIconName="teammatesFilledSmall"
                counterKey={sharedWithMeCounterKey}
                countersVisibility={countersVisibility}
                indentLevel={1}
                workspaceDragItem={{
                  type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
                  id: WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME,
                  isLast: index === lastIndex,
                }}
                interactionId="workspace-user-shared"
              >
                <Trans id="workspace-sharedwithme">Shared with me</Trans>
              </WorkspaceGenericItemContainer>
            );
          }

          case WorkspaceFolderNestedLabelsEnum.DISCUSSIONS: {
            return (
              hasInternalDiscussions && (
                <WorkspaceGenericItemContainer
                  shouldAlphabetizeWorkspace={false}
                  canBeHidden={false}
                  shouldHideMoreMenu={false}
                  trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.LABELS}
                  key={item.id}
                  panelId={panelId}
                  onWorkspaceItemDrop={onWorkspaceItemDrop}
                  to={buildTeammatePath(user.id, TeammatesFoldersEnum.DISCUSSIONS, InboxesFiltersEnum.OPEN)}
                  iconName="discussionSmall"
                  selectedIconName="discussionFilledSmall"
                  counterKey={discussionsCounterKey}
                  countersVisibility={countersVisibility}
                  indentLevel={1}
                  label={WorkspaceFolderNestedLabelsEnum.DISCUSSIONS}
                  workspaceDragItem={{
                    type: WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER,
                    id: WorkspaceFolderNestedLabelsEnum.DISCUSSIONS,
                    isLast: index === lastIndex,
                  }}
                  interactionId="workspace-user-discussions"
                >
                  <Trans id="workspace-discussions">Discussions</Trans>
                </WorkspaceGenericItemContainer>
              )
            );
          }

          default:
            return null;
        }
      })
      .compact()
      .value()
  );
}
